/**
 * PostCreateModal — 게시글 작성 다이얼로그
 *
 * notice 게시판일 때는 고정(Pin) + 중요도(Importance) 옵션을 추가로 표시.
 * issue 게시판일 때는 상태 / 우선순위 / 마감일 / 담당자 옵션을 표시.
 *
 * 각 유형별 옵션은 fieldsets/ 디렉토리의 서브폼 컴포넌트로 분리.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Trans, t } from '@lingui/macro';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useBoardContext } from '../../context/BoardContext';
import { useAppUIContext } from '../../context/AppUIContext';
import { createPost, updatePost, uploadBoardImage, uploadPostAttachment, notifyNewPost, getUsedTags, fetchPageTree } from '../../services/BoardService';
import { askAI } from '../../services/AiMemoAnalyzer';
import { Post, PostAttachment } from '../../models/Board';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  PostAuthor, IssueStatus, IssuePriority,
} from '../../models/Board';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { Message } from '../../models/Message';
import { toast } from '../../components/ui/use-toast';
import NoticeFieldSet from './fieldsets/NoticeFieldSet';
import IssueFieldSet from './fieldsets/IssueFieldSet';
import BoardRichEditor from './BoardRichEditor';
import { ChatFilePickerModal } from './ChatFilePickerModal';
import { extractMentionedUserIds, hasMentionAll, MentionUser } from './MentionSuggestion';
import { useDraftAutoSave, DraftData } from './useDraftAutoSave';
import TagInput from './TagInput';
import { Checkbox } from '../../components/ui/checkbox';
import { useMyRole } from '../../context/WorkspacePermissionContext';
import { useWorkspaceSettings } from '../../context/WorkspaceSettingsContext';
import { canUseAllMention, canWritePost } from '../../models/Board';
import { UserAvatar } from '../components/UserAvatar';
import {
  Users, AtSign, FolderTree, Bell, FolderOpen, FileText, Check, Pin, Plus, BookOpen, Loader2,
  Paperclip, X, Download, Sparkles, Eye,
} from 'lucide-react';
import { formatDraftSavedAgo } from './useDraftAutoSave';
import PostPreviewModal from './PostPreviewModal';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 수정 모드 — 게시글 객체를 전달하면 그 글의 수정 모달로 동작 */
  editingPost?: Post | null;
  /** 수정 저장 성공 시 호출 — 상위에서 selectedPost 등 동기화 */
  onSaved?: (post: Post) => void;
}

const PostCreateModal: React.FC<Props> = ({ open, onOpenChange, editingPost, onSaved }) => {
  const isEdit = !!editingPost;
  const { selectedBoard, refreshPosts } = useBoardContext();
  const { activeWorkspaceId } = useAppUIContext();
  const [title, setTitle] = useState('');
  const [editorJson, setEditorJson] = useState<any>(null);
  const [editorPlainText, setEditorPlainText] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isPinned, setIsPinned] = useState(false);
  const [importance, setImportance] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 채널 게시판에서 게시글 작성 시 채팅에 AutoMessage 카드 알림 보낼지 여부 (기본 ON)
  const [notifyChat, setNotifyChat] = useState(true);

  // 초안 복원 시 에디터를 리마운트하기 위한 key
  const [editorKey, setEditorKey] = useState(0);
  const [initialContent, setInitialContent] = useState<any>(undefined);

  // Issue 전용 상태
  const [issueStatus, setIssueStatus] = useState<IssueStatus>('todo');
  const [issuePriority, setIssuePriority] = useState<IssuePriority>('none');
  const [issueDueDate, setIssueDueDate] = useState('');
  const [assigneeId, setAssigneeId] = useState('');  // '' = 미배정

  // Wiki 전용 상태
  // 위키 페이지 계층 — 부모 페이지 선택 (빈 문자열 = 루트)
  const [parentPostId, setParentPostId] = useState<string>('');
  // 위키 페이지 목록 (부모 선택 + 멘션 자동완성용)
  const [wikiPages, setWikiPages] = useState<Post[]>([]);

  // 일반 파일 첨부 (이미지 외 PDF/DOCX/ZIP 등). 본문 이미지는 BoardRichEditor 안에서 별도 처리.
  const [attachments, setAttachments] = useState<PostAttachment[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = React.useRef<HTMLInputElement>(null);
  // 채팅에서 파일 가져오기 (채널 게시판일 때만 활성)
  const [showChatFilePicker, setShowChatFilePicker] = useState(false);

  const handleAttachmentSelect = useCallback(async (filesList: FileList | null) => {
    if (!filesList || filesList.length === 0 || !My.user?.id || !selectedBoard) return;
    setUploadingAttachment(true);
    try {
      const uploaded: PostAttachment[] = [];
      for (const f of Array.from(filesList)) {
        const r = await uploadPostAttachment(f, My.user.id, selectedBoard.id);
        uploaded.push({ ...r, source: 'local' });
      }
      setAttachments(prev => [...prev, ...uploaded]);
    } catch (err) {
      console.error('[PostCreateModal] 첨부 업로드 실패:', err);
      toast({ title: t`Attachment upload failed`, variant: 'destructive' });
    } finally {
      setUploadingAttachment(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  }, [selectedBoard]);

  const removeAttachment = (idx: number) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const isNoticeBoard = selectedBoard?.type === 'notice';
  const isIssueBoard = selectedBoard?.type === 'issue';
  const isWikiBoard = selectedBoard?.type === 'wiki';

  // 멘션 권한 (게시판 정책 + 사용자 역할)
  const myRole = useMyRole();
  const wsSettings = useWorkspaceSettings();
  const wsManagerIds = wsSettings.board_manager_user_ids;
  // @개인 멘션은 작성권한과 자동 일치 — 별도 권한 분리 안 함
  const canTargetMention = !!selectedBoard && canWritePost(selectedBoard, myRole ?? 'guest', My.user?.id, wsManagerIds);
  const canAllMention = !!selectedBoard && canUseAllMention(selectedBoard, myRole ?? 'guest', My.user?.id, wsManagerIds);
  /** 본문에서 @전체 sentinel 검출 여부 — 토글 버튼 대신 본문 파싱으로 자동 계산 */
  const mentionAllInBody = useMemo(() => {
    if (!editorJson || !canAllMention) return false;
    return hasMentionAll(editorJson);
  }, [editorJson, canAllMention]);

  /** 워크스페이스 멤버 목록 (@멘션용) */
  const [mentionMembers, setMentionMembers] = useState<MentionUser[]>([]);
  /** 기존 사용된 태그 (자동완성용) */
  const [usedTags, setUsedTags] = useState<string[]>([]);

  /** 본문 미리보기 모달 (body + TOC) */
  const [previewOpen, setPreviewOpen] = useState(false);

  /** 본문에서 실시간 추출한 멘션된 사용자 — 작성 중에도 칩으로 표시 */
  const mentionedUsersInBody = useMemo(() => {
    if (!editorJson || !canTargetMention) return [] as MentionUser[];
    const ids = extractMentionedUserIds(editorJson);
    if (ids.length === 0) return [];
    return ids
      .map(uid => mentionMembers.find(m => m.id === uid))
      .filter((m): m is MentionUser => !!m);
  }, [editorJson, canTargetMention, mentionMembers]);

  useEffect(() => {
    if (!open || !activeWorkspaceId) return;
    // 권한 없으면 멘션 후보 자체를 빈 배열로 → 에디터의 @ 드롭다운 비활성
    if (!canTargetMention) {
      setMentionMembers([]);
      return;
    }
    Fire.ws.getWorkspaceMembers(activeWorkspaceId).then((members) => {
      setMentionMembers(
        members
          .filter(m => m.id !== My.user?.id)
          .map(m => ({
            id: m.id,
            name: m.user.name,
            profileUrl: m.user.photoUrl ?? null,
          }))
      );
    }).catch(() => {});

    // 기존 사용 태그 로드
    if (selectedBoard?.id) {
      getUsedTags(selectedBoard.id).then(setUsedTags).catch(() => {});
    }

    // 위키 게시판: 페이지 목록 로드 (부모 선택 + 위키링크 자동완성용)
    if (selectedBoard?.type === 'wiki') {
      fetchPageTree(selectedBoard.id).then(setWikiPages).catch(() => {});
    } else {
      setWikiPages([]);
    }
  }, [open, activeWorkspaceId, selectedBoard?.id, selectedBoard?.type, canTargetMention]);

  /** 채널 멤버 목록 (issue 게시판은 channel 스코프에서만 생성 가능하므로 channel_id 존재) */
  const channelMembers = useMemo(() => {
    if (!isIssueBoard || !selectedBoard?.channel_id) return [];
    const room = My.Rooms.get(selectedBoard.channel_id);
    if (!room) return [];
    return room.users.map(u => ({ id: u.id, name: u.name, photoUrl: u.photoUrl }));
  }, [isIssueBoard, selectedBoard?.channel_id]);

  const resetForm = () => {
    setTitle('');
    setEditorJson(null);
    setEditorPlainText('');
    setTags([]);
    setIsPinned(false);
    setImportance('');
    setIssueStatus('todo');
    setIssuePriority('none');
    setIssueDueDate('');
    setAssigneeId('');
    setParentPostId('');
    setInitialContent(undefined);
    setEditorKey(prev => prev + 1);
    setNotifyChat(true);
    setAttachments([]);
  };

  // ── 임시 저장 훅 ──
  const { clearDraft, lastSavedAt } = useDraftAutoSave({
    boardId: selectedBoard?.id,
    title,
    editorJson,
    tags,
    isOpen: open,
    onRestore: useCallback((draft: DraftData) => {
      setTitle(draft.title);
      setTags(Array.isArray(draft.tags) ? draft.tags : []);
      if (draft.editorJson) {
        setInitialContent(draft.editorJson);
        setEditorJson(draft.editorJson);
        setEditorKey(prev => prev + 1);
      }
    }, []),
  });

  /** 인디케이터 텍스트: "임시 저장됨 · 방금 전" — savedAt 변할 때마다 1분 단위로 재계산 */
  const draftSavedAgo = useMemo(() => formatDraftSavedAgo(lastSavedAt), [lastSavedAt]);

  /** 위키 페이지 inline tree picker용 — parent_post_id 기준 트리 구조 */
  const wikiPageOptions = useMemo(() => {
    if (!isWikiBoard) return [] as Array<{ id: string; title: string; depth: number }>;
    const byParent = new Map<string | null, Post[]>();
    wikiPages.forEach(p => {
      const key = p.parent_post_id ?? null;
      const arr = byParent.get(key) ?? [];
      arr.push(p);
      byParent.set(key, arr);
    });
    byParent.forEach(group => group.sort((a, b) => a.title.localeCompare(b.title)));

    const out: Array<{ id: string; title: string; depth: number }> = [];
    const seen = new Set<string>();
    const visit = (parentId: string | null, depth: number) => {
      const list = byParent.get(parentId) ?? [];
      for (const p of list) {
        if (seen.has(p.id) || depth > 4) continue;
        seen.add(p.id);
        out.push({ id: p.id, title: p.title || t`Untitled`, depth });
        visit(p.id, depth + 1);
      }
    };
    visit(null, 0);
    return out;
  }, [isWikiBoard, wikiPages]);

  const handleSubmit = async () => {
    if (!title.trim() || !selectedBoard || submitting || !My.user) return;
    setSubmitting(true);
    try {
      const author: PostAuthor = {
        uid: My.user.id,
        name: My.user.name,
        photoUrl: My.user.photoUrl ?? null,
      };
      const tagList = tags;

      // metadata 구성 — 수정 모드에선 기존 metadata 와 머지
      const metadata: Record<string, any> = isEdit ? { ...(editingPost?.metadata ?? {}) } : {};
      if (isNoticeBoard) {
        if (importance) metadata.importance = importance;
        else delete metadata.importance;
      }
      if (isIssueBoard) {
        metadata.status = issueStatus;
        metadata.priority = issuePriority;
        if (issueDueDate) metadata.due_date = issueDueDate;
        else delete metadata.due_date;
        if (assigneeId) {
          const member = channelMembers.find(m => m.id === assigneeId);
          if (member) {
            metadata.assignee = {
              uid: member.id,
              name: member.name,
              photoUrl: member.photoUrl ?? null,
            };
          }
        } else {
          delete metadata.assignee;
        }
      }

      // mentioned_user_ids 추출 (권한 없으면 무시 — 서버측에서도 한 번 더 검증)
      const mentionedUserIds = (canTargetMention && editorJson) ? extractMentionedUserIds(editorJson) : [];
      const effectiveMentionAll = canAllMention && mentionAllInBody;

      // ── 수정 모드: updatePost ──
      if (isEdit && editingPost) {
        const updated = await updatePost(editingPost.id, {
          title: title.trim(),
          content: editorJson ?? undefined,
          plain_text: editorPlainText.trim() || undefined,
          tags: tagList,
          is_pinned: (isNoticeBoard || isWikiBoard) ? isPinned : editingPost.is_pinned,
          metadata: Object.keys(metadata).length > 0 ? metadata : null,
          parent_post_id: isWikiBoard ? (parentPostId || null) : editingPost.parent_post_id,
          mentioned_user_ids: mentionedUserIds.length > 0 ? mentionedUserIds : null,
          attachments,
        });
        clearDraft();
        resetForm();
        onOpenChange(false);
        refreshPosts(selectedBoard.id);
        onSaved?.(updated);
        toast({ title: t`Post updated` });
        return;
      }

      // ── 생성 모드: createPost ──
      const createdPost = await createPost({
        board_id: selectedBoard.id,
        workspace_id: selectedBoard.workspace_id,
        title: title.trim(),
        content: editorJson ?? undefined,
        plain_text: editorPlainText.trim() || undefined,
        author,
        tags: tagList.length > 0 ? tagList : undefined,
        is_pinned: (isNoticeBoard || isWikiBoard) ? isPinned : false,
        metadata: Object.keys(metadata).length > 0 ? metadata : (isIssueBoard ? { status: 'todo', priority: 'none' } : undefined),
        parent_post_id: isWikiBoard && parentPostId ? parentPostId : null,
        mentioned_user_ids: mentionedUserIds.length > 0 ? mentionedUserIds : undefined,
        mention_all: effectiveMentionAll,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      // 알림 전송 (비동기 — 실패해도 무시) — 항상 호출.
      // 수신자별 prefs(notify_new_post) + 멘션 여부로 functions(board-notify)가 대상 필터링.
      notifyNewPost(
        selectedBoard.workspace_id,
        selectedBoard.id,
        selectedBoard.name,
        title.trim(),
        createdPost.id,
        mentionedUserIds,
        effectiveMentionAll,
      );

      // 채널 게시판 + 사용자가 알림 체크박스 ON일 때만 채팅에 AutoMessage 카드 삽입
      if (createdPost && selectedBoard.channel_id && notifyChat) {
        try {
          const room = My.Rooms.get(selectedBoard.channel_id);
          if (room && My.user) {
            const sysMsg = new Message(room, My.user);
            sysMsg.autoMessage = isWikiBoard ? 'wiki-page-created' : 'post-created';
            sysMsg.text = title.trim();
            // PostTitleLink 가 "boardId:postId" 형태로 파싱
            sysMsg.autoMessageItemId = `${selectedBoard.id}:${createdPost.id}`;
            await Fire.msg.saveMessage(sysMsg);
          }
        } catch (err) {
          console.warn('[PostCreate] AutoMessage 전송 실패:', err);
        }
      }

      // 임시 저장 삭제 + 리셋 & 닫기
      clearDraft();
      resetForm();
      onOpenChange(false);
      refreshPosts(selectedBoard.id);
      toast({ title: t`Post created` });
    } catch (err) {
      console.error('[PostCreate] 게시글 처리 실패:', err);
      toast({ title: t`${isEdit ? 'Failed to update post' : 'Failed to create post'}`, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // 모달이 열릴 때 초기화
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) resetForm();
    onOpenChange(newOpen);
  };

  // 수정 모드: open=true 가 될 때마다 editingPost 값으로 prefill
  useEffect(() => {
    if (!open || !editingPost) return;
    setTitle(editingPost.title);
    setEditorJson(editingPost.content ?? null);
    setEditorPlainText(editingPost.plain_text ?? '');
    setTags(editingPost.tags ?? []);
    setIsPinned(editingPost.is_pinned);
    setImportance((editingPost.metadata as any)?.importance ?? '');
    setIssueStatus(((editingPost.metadata as any)?.status as IssueStatus) ?? 'todo');
    setIssuePriority(((editingPost.metadata as any)?.priority as IssuePriority) ?? 'none');
    setIssueDueDate((editingPost.metadata as any)?.due_date ?? '');
    setAssigneeId((editingPost.metadata as any)?.assignee?.uid ?? '');
    setParentPostId(editingPost.parent_post_id ?? '');
    setAttachments(editingPost.attachments ?? []);
    setInitialContent(editingPost.content ?? undefined);
    setEditorKey(prev => prev + 1);
  }, [open, editingPost?.id]);

  // 본문 에디터 (위키/비위키 공통) — JSX를 변수로 추출해 양쪽 레이아웃에서 재사용
  const editorBlock = (
    <BoardRichEditor
      key={editorKey}
      initialContent={initialContent}
      onChange={(json, plainText) => {
        setEditorJson(json);
        setEditorPlainText(plainText);
      }}
      placeholder={isWikiBoard ? t`Enter content... (@ to mention / wiki page link)` : t`Enter content... (@ to mention)`}
      minHeight={isWikiBoard ? 320 : 180}
      onImageUpload={selectedBoard ? async (file) => {
        const userId = My.user?.id;
        if (!userId) throw new Error('Login required');
        return uploadBoardImage(file, userId, selectedBoard.id);
      } : undefined}
      mentionMembers={mentionMembers}
      wikiPages={isWikiBoard ? wikiPages.map(p => ({ kind: 'page' as const, id: p.id, title: p.title })) : undefined}
      canMentionAll={canAllMention}
    />
  );

  // 일반 파일 첨부 영역 — 에디터 바로 아래. 이미지는 에디터 안에서, 일반 파일(PDF/DOCX/ZIP 등)은 여기.
  const attachmentBlock = (
    <div className="flex flex-col gap-2 pt-1">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => attachmentInputRef.current?.click()}
          disabled={uploadingAttachment}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-meta-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-60"
        >
          {uploadingAttachment ? <Loader2 className="size-3.5 animate-spin" /> : <Paperclip className="size-3.5" />}
          <Trans>Attach file</Trans>
        </button>
        {/* 채널 게시판일 때만 — 댓글과 동일 패턴 */}
        {selectedBoard?.channel_id && (
          <button
            type="button"
            onClick={() => setShowChatFilePicker(true)}
            disabled={uploadingAttachment}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-meta-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-60"
          >
            <FolderOpen className="size-3.5" />
            <Trans>Import from chat</Trans>
          </button>
        )}
        <input
          ref={attachmentInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={e => handleAttachmentSelect(e.target.files)}
        />
        <span className="text-meta-sm text-muted-foreground"><Trans>PDF · DOCX · ZIP etc. (images in content editor)</Trans></span>
      </div>
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachments.map((att, idx) => (
            <div
              key={`${att.url}-${idx}`}
              className="group inline-flex items-center gap-2 bg-muted/40 rounded-lg pl-2 pr-1 py-1.5 text-xs max-w-[280px]"
            >
              {att.group === 'image' ? (
                <img src={att.url} alt={att.filename} className="size-8 rounded object-cover shrink-0" />
              ) : (
                <FileText className="size-4 text-muted-foreground shrink-0" />
              )}
              <span className="truncate flex-1 min-w-0 text-foreground/90">{att.filename}</span>
              <button
                type="button"
                onClick={() => removeAttachment(idx)}
                className="size-5 rounded hover:bg-destructive/15 text-muted-foreground hover:text-destructive transition-colors flex items-center justify-center"
                title={t`Remove`}
              >
                <X className="size-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const mentionInlineList = (() => {
    const displayList = mentionAllInBody ? mentionMembers : mentionedUsersInBody;
    if (displayList.length === 0) return null;
    return (
      <div className="flex items-center gap-2 flex-wrap pl-1">
        <AtSign className="size-3.5 text-muted-foreground shrink-0" />
        <span className="text-meta-sm font-medium text-muted-foreground shrink-0"><Trans>Mention:</Trans></span>
        {mentionAllInBody && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-meta bg-badge-warn-bg text-badge-warn ring-1 ring-badge-warn/30">
            <Users className="size-3" />
            <Trans>Everyone</Trans>
          </span>
        )}
        {displayList.map(u => (
          <span
            key={u.id}
            className="mention inline-flex items-center gap-1 text-meta"
          >
            <UserAvatar src={u.profileUrl} name={u.name} className="size-4" />
            @{u.name}
          </span>
        ))}
      </div>
    );
  })();

  // AI 자동 태그 — 본문 내용을 분석해서 3~5개 태그 추출 (기존 사용 태그 우선)
  const [aiTagsLoading, setAiTagsLoading] = useState(false);
  const handleGenerateTagsByAI = useCallback(async () => {
    const body = editorPlainText.trim();
    if (!body && !title.trim()) {
      toast({ title: t`Please enter content or a title first` });
      return;
    }
    setAiTagsLoading(true);
    try {
      const prompt = [
        'Extract 3~5 tags suitable for the following post.',
        '- Keep each tag to 1~2 words in Korean',
        '- If existing tags are available, prioritize them',
        '- Response should be a single line separated by commas (e.g., meeting, schedule, marketing)',
        '',
        `[Title] ${title.trim() || '(none)'}`,
        '',
        '[Body]',
        body || '(none)',
        '',
        usedTags.length > 0 ? `[Existing tags] ${usedTags.join(', ')}` : '',
        '',
        'Tags:',
      ].filter(Boolean).join('\n');
      const raw = await askAI(prompt, 0.3);
      const cleaned = raw
        .split(/[,،、]/)
        .map(t => t.trim().replace(/^#/, '').replace(/^"|"$/g, ''))
        .filter(t => t.length > 0 && t.length <= 20)
        .slice(0, 6);
      if (cleaned.length === 0) {
        toast({ title: t`AI could not find any tags`, variant: 'destructive' });
        return;
      }
      // 기존 태그와 병합 (중복 제거)
      setTags(prev => {
        const set = new Set(prev);
        cleaned.forEach(t => set.add(t));
        return Array.from(set);
      });
      toast({ title: t`AI added ${cleaned.length} tags` });
    } catch (err) {
      console.error('[PostCreateModal] AI 태그 생성 실패:', err);
      toast({ title: t`Failed to generate AI tags`, variant: 'destructive' });
    } finally {
      setAiTagsLoading(false);
    }
  }, [editorPlainText, title, usedTags]);

  const tagsBlock = (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 justify-between flex-wrap">
        <span className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
          <Trans>Tags</Trans>
        </span>
        <button
          type="button"
          onClick={handleGenerateTagsByAI}
          disabled={aiTagsLoading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border text-meta-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/30 hover:bg-primary/5 transition-colors cursor-pointer disabled:opacity-60"
          title={t`AI analyzes content and recommends tags`}
        >
          {aiTagsLoading ? <Loader2 className="size-3.5 animate-spin" /> : <Sparkles className="size-3.5" />}
          <Trans>AI auto-generate</Trans>
        </button>
      </div>
      <TagInput
        value={tags}
        onChange={setTags}
        defaultTags={selectedBoard?.default_tags ?? []}
        usedTags={usedTags}
        placeholder={t`Enter tags... (add with Enter/comma)`}
      />
    </div>
  );

  // 위키 분기: 2-column 레이아웃 (좌측 에디터 풀폭 + 우측 aside 알림 풀폭)
  if (isWikiBoard) {
    const hasNotifyOptions = !!(selectedBoard?.channel_id || mentionedUsersInBody.length > 0 || mentionAllInBody);
    const author = My.user;

    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[1100px] w-[95vw] max-h-[92vh] overflow-hidden p-0 flex flex-col gap-0">
          {/* ── Chrome / Header ── */}
          <DialogHeader className="flex flex-row items-center gap-2 px-5 h-12 border-b border-border shrink-0 space-y-0">
            <DialogTitle className="text-body font-semibold text-foreground">{isEdit ? t`Edit wiki page` : t`New wiki page`}</DialogTitle>
            {/* 임시 저장됨 인디케이터 */}
            {draftSavedAgo && (
              <span className="hidden md:inline-flex items-center gap-1.5 ml-3 text-meta-sm text-muted-foreground">
                <span className="size-1.5 rounded-full bg-badge-success" />
                <Trans>Auto-saved · {draftSavedAgo}</Trans>
              </span>
            )}
          </DialogHeader>

          <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
            {/* ── 좌측 본문 (에디터 풀폭) ── */}
            <div className="flex-1 min-w-0 overflow-y-auto px-6 py-5 space-y-4">
              {/* 제목 — placeholder만, 큰 글씨 */}
              <Input
                placeholder={t`Page title`}
                value={title}
                onChange={e => setTitle(e.target.value)}
                autoFocus
                className="h-auto text-display-lg font-bold tracking-tight border-0 px-0 py-1 focus-visible:ring-0 placeholder:text-muted-foreground/50"
              />

              {/* 작성자 메타 — 우측 끝에 핀 아이콘 토글 */}
              {author && (
                <div className="flex items-center gap-3 mt-1">
                  <UserAvatar src={author.photoUrl ?? null} name={author.name} className="size-8" />
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-foreground leading-none">{author.name}</span>
                    <span className="text-meta-sm text-muted-foreground mt-1"><Trans>Creation date is recorded when posted</Trans></span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsPinned(v => !v)}
                    data-state={isPinned ? 'on' : undefined}
                    title={isPinned ? t`Unpin from sidebar` : t`Pin to sidebar`}
                    aria-label={isPinned ? t`Unpin from sidebar` : t`Pin to sidebar`}
                    className="ml-auto inline-flex items-center justify-center size-8 rounded-full text-muted-foreground hover:bg-muted/60 transition-colors data-[state=on]:bg-badge-caution-bg data-[state=on]:text-badge-caution"
                  >
                    <Pin className="size-4" strokeWidth={isPinned ? 2.5 : 1.5} fill={isPinned ? 'currentColor' : 'none'} />
                  </button>
                </div>
              )}

              {/* Properties row — 멘션 칩만 (핀은 작성자 행으로 이동, 태그는 본문 아래로 이동) */}
              {(mentionAllInBody || mentionedUsersInBody.length > 0) && (
                <div className="flex items-center gap-1.5 flex-wrap pt-1">
                  <span className="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-full text-meta-sm text-muted-foreground">
                    <AtSign className="size-3" />
                    <span><Trans>Mention</Trans></span>
                    {mentionAllInBody && (
                      <span className="inline-flex items-center gap-1 px-1.5 rounded-full text-meta-sm bg-badge-warn-bg text-badge-warn">
                        <Users className="size-2.5" /><Trans>Everyone</Trans>
                      </span>
                    )}
                    {mentionedUsersInBody.map(u => (
                      <span key={u.id} className="inline-flex items-center gap-1 text-foreground">
                        <UserAvatar src={u.profileUrl} name={u.name} className="size-4" />
                        @{u.name}
                      </span>
                    ))}
                  </span>
                </div>
              )}

              <hr className="border-border my-2" />

              {/* 본문 에디터 */}
              {editorBlock}

              {/* 일반 파일 첨부 */}
              {attachmentBlock}

              {/* 태그 (본문 아래 — AI 자동 생성 가능) */}
              {tagsBlock}
            </div>

            {/* ── 우측 aside ── */}
            <aside className="w-full md:w-[300px] shrink-0 border-t md:border-t-0 md:border-l border-border bg-muted/20 overflow-y-auto px-4 py-4 space-y-5">
              {/* 부모 페이지 — inline tree picker */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  <FolderTree className="size-3" />
                  <span><Trans>Parent page</Trans></span>
                </div>
                <div className="rounded-lg border border-border bg-card p-1 space-y-px max-h-[220px] overflow-y-auto">
                  <button
                    type="button"
                    onClick={() => setParentPostId('')}
                    data-state={parentPostId === '' ? 'on' : undefined}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-meta text-muted-foreground hover:bg-muted/60 data-[state=on]:bg-primary/10 data-[state=on]:text-primary data-[state=on]:font-semibold transition-colors"
                  >
                    <FolderOpen className="size-3 shrink-0 opacity-70" />
                    <span className="flex-1 text-left truncate">— <Trans>Root page</Trans> —</span>
                    {parentPostId === '' && <Check className="size-3 shrink-0 text-primary" />}
                  </button>
                  {wikiPageOptions.map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setParentPostId(opt.id)}
                      data-state={parentPostId === opt.id ? 'on' : undefined}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-meta text-foreground hover:bg-muted/60 data-[state=on]:bg-primary/10 data-[state=on]:text-primary data-[state=on]:font-semibold transition-colors"
                      style={{ paddingLeft: opt.depth * 12 + 8 }}
                    >
                      <FileText className="size-3 shrink-0 opacity-70" />
                      <span className="flex-1 text-left truncate">{opt.title}</span>
                      {parentPostId === opt.id && <Check className="size-3 shrink-0 text-primary" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* 알림 — 박스 풀폭 */}
              {hasNotifyOptions && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
                    <Bell className="size-3" />
                    <span><Trans>Notifications</Trans></span>
                  </div>
                  {selectedBoard?.channel_id && (
                    <label className="w-full flex items-start gap-2 px-3 py-2.5 rounded-lg border border-border bg-card hover:bg-muted/50 cursor-pointer select-none transition-colors">
                      <Checkbox
                        checked={notifyChat}
                        onCheckedChange={v => setNotifyChat(v === true)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-meta font-semibold text-foreground"><Trans>Send notification to chat</Trans></div>
                        <div className="text-meta-sm text-muted-foreground mt-0.5 leading-snug">
                          <Trans>Sends a wiki page created card to the channel</Trans>
                        </div>
                      </div>
                    </label>
                  )}
                </div>
              )}

              {/* 작성 팁 */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  <BookOpen className="size-3" />
                  <span><Trans>Writing tips</Trans></span>
                </div>
                <div className="text-meta-sm text-muted-foreground leading-relaxed space-y-1.5">
                  <p className="m-0">
                    <Trans>Use <strong>@</strong> to mention members, <strong>[[</strong> to link other wiki pages.</Trans>
                  </p>
                  <p className="m-0"><Trans>Auto-saved every 3 seconds.</Trans></p>
                </div>
              </div>
            </aside>
          </div>

          {/* 푸터 액션 */}
          <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-border shrink-0 bg-background">
            <Button
              variant="ghost"
              onClick={() => setPreviewOpen(true)}
              disabled={!editorJson && !title.trim()}
              className="gap-1.5"
            >
              <Eye className="size-4" />
              <Trans>Preview</Trans>
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                <Trans>Cancel</Trans>
              </Button>
              <Button onClick={handleSubmit} disabled={!title.trim() || submitting}>
                {submitting ? (isEdit ? t`Saving...` : t`Creating...`) : (isEdit ? t`Save` : t`Post`)}
              </Button>
            </div>
          </div>
        </DialogContent>
        {selectedBoard?.channel_id && (
          <ChatFilePickerModal
            open={showChatFilePicker}
            channelId={selectedBoard.channel_id}
            onClose={() => setShowChatFilePicker(false)}
            onPick={(att) => {
              setAttachments(prev => [...prev, att]);
              setShowChatFilePicker(false);
            }}
          />
        )}
        <PostPreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title={title}
          editorJson={editorJson}
          tags={tags}
          isWiki
          wikiPages={wikiPageOptions.map(o => ({ kind: 'page' as const, id: o.id, title: o.title }))}
        />
      </Dialog>
    );
  }

  // 비위키 분기: 기존 단일 컬럼 레이아웃 유지
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[1100px] w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? t`Edit post` : t`Create new post`}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <Input
            placeholder={t`Enter title`}
            value={title}
            onChange={e => setTitle(e.target.value)}
            autoFocus
          />

          {editorBlock}

          {attachmentBlock}

          {mentionInlineList}

          {tagsBlock}

          {isNoticeBoard && (
            <NoticeFieldSet
              isPinned={isPinned}
              onIsPinnedChange={setIsPinned}
              importance={importance}
              onImportanceChange={setImportance}
            />
          )}

          {isIssueBoard && (
            <IssueFieldSet
              issueStatus={issueStatus}
              onIssueStatusChange={setIssueStatus}
              issuePriority={issuePriority}
              onIssuePriorityChange={setIssuePriority}
              issueDueDate={issueDueDate}
              onIssueDueDateChange={setIssueDueDate}
              assigneeId={assigneeId}
              onAssigneeIdChange={setAssigneeId}
              channelMembers={channelMembers}
            />
          )}

          {/* 알림 옵션 + 액션 */}
          <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              {selectedBoard?.channel_id && (
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                  <Checkbox
                    checked={notifyChat}
                    onCheckedChange={v => setNotifyChat(v === true)}
                  />
                  <span><Trans>Send notification to chat</Trans></span>
                </label>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => setPreviewOpen(true)}
                disabled={!editorJson && !title.trim()}
                className="gap-1.5"
              >
                <Eye className="size-4" />
                <Trans>Preview</Trans>
              </Button>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                <Trans>Cancel</Trans>
              </Button>
              <Button onClick={handleSubmit} disabled={!title.trim() || submitting}>
                {submitting ? (isEdit ? t`Saving...` : t`Creating...`) : (isEdit ? t`Save` : t`Post`)}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
      {selectedBoard?.channel_id && (
        <ChatFilePickerModal
          open={showChatFilePicker}
          channelId={selectedBoard.channel_id}
          onClose={() => setShowChatFilePicker(false)}
          onPick={(att) => {
            setAttachments(prev => [...prev, att]);
            setShowChatFilePicker(false);
          }}
        />
      )}
      <PostPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={title}
        editorJson={editorJson}
        tags={tags}
        isWiki={false}
      />
    </Dialog>
  );
};

export default PostCreateModal;
